import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Collapse,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RestoreIcon from "@mui/icons-material/Restore";
import StorefrontIcon from "@mui/icons-material/Storefront";
import axios from "axios";
import { tokens } from "../../../../../../theme";
import type { OrganizerCardProps } from "../../../types/types";
import type { BookingData } from "../../../../types/view-booking";
import apiService from "../../../../../../service/Api";
import { showSuccessToast, showErrorToast } from "../../../../../../components/common/ShowToast";

interface WhatsAppDialogProps {
  open: boolean;
  onClose: () => void;
  client?: OrganizerCardProps;
  currentUserName: string;
  bookingData?: BookingData | null;
}

interface SendWhatsAppResponse {
  ok: boolean;
  message?: string;
  error?: string;
}

const normalizePhoneForWhatsapp = (phone?: string) => {
  if (!phone) return "";
  let cleaned = phone.replace(/[^\d+]/g, "");
  cleaned = cleaned.replace(/^\+/, "");
  if (cleaned.startsWith("0")) {
    cleaned = `263${cleaned.slice(1)}`;
  }
  return cleaned;
};

const DEFAULT_WHATSAPP_TEMPLATE =
  "Hi [Client Name], I am [Your Name] from Muzukuru Funeral Streaming. I am reaching out to you regarding [Topic].";

export default function WhatsAppDialog({
  open,
  onClose,
  client,
  currentUserName,
  bookingData,
}: WhatsAppDialogProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [whatsappTemplate, setWhatsappTemplate] = useState("");
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [originalMessage, setOriginalMessage] = useState("");

  // AI assistant states
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiGeneratedText, setAiGeneratedText] = useState("");
  const [aiError, setAiError] = useState("");

  // Client Name Extraction (First Name Only)
  const clientNameStr = client?.name
    ? client.name.trim().split(/\s+/)[0]
    : "Client";
  const userNameStr = currentUserName;
  const deceasedNameStr =
    bookingData?.beloved?.name && bookingData.beloved.name !== "N/A"
      ? bookingData.beloved.name
      : "";
  const serviceTypeStr = bookingData?.service || "Funeral";

  const getTopic = () => {
    let topicStr = "the streaming service";
    if (bookingData) {
      if (
        bookingData.service === "Conference" &&
        bookingData.conferenceDetails?.conference_title
      ) {
        topicStr = `the streaming service for "${bookingData.conferenceDetails.conference_title}"`;
      } else if (
        bookingData.service === "Church" &&
        bookingData.churchDetails?.church_name
      ) {
        topicStr = `the streaming service for "${bookingData.churchDetails.church_name}"`;
      } else if (bookingData.beloved?.name && bookingData.beloved.name !== "N/A") {
        topicStr = `the streaming service for the late ${bookingData.beloved.name}`;
      }
    }
    return topicStr;
  };

  const topicStr = getTopic();

  const getStoredTemplate = () => {
    const stored = localStorage.getItem("whatsapp_booking_template");
    if (!stored) {
      return DEFAULT_WHATSAPP_TEMPLATE;
    }
    return stored;
  };

  const compileTemplate = (
    templateStr: string,
    clientName: string,
    userName: string,
    deceasedName: string,
    serviceType: string,
    topic: string
  ) => {
    return templateStr
      .replace(/\[Client Name\]/g, clientName)
      .replace(/\[Your Name\]/g, userName)
      .replace(/\[Deceased Name\]/g, deceasedName)
      .replace(/\[Service Type\]/g, serviceType)
      .replace(/\[Topic\]/g, topic);
  };

  useEffect(() => {
    if (open) {
      const template = getStoredTemplate();
      setWhatsappTemplate(template);
      const compiled = compileTemplate(
        template,
        clientNameStr,
        userNameStr,
        deceasedNameStr,
        serviceTypeStr,
        topicStr
      );
      setWhatsappMessage(compiled);
      setOriginalMessage(compiled);
      setShowTemplateEditor(false);

      // Reset AI states
      setAiPanelOpen(false);
      setAiPrompt("");
      setAiGeneratedText("");
      setAiError("");
    }
  }, [
    open,
    client,
    bookingData,
    currentUserName,
    clientNameStr,
    deceasedNameStr,
    serviceTypeStr,
    topicStr,
    userNameStr,
  ]);

  const handleTemplateChange = (newTemplate: string) => {
    setWhatsappTemplate(newTemplate);
    const compiled = compileTemplate(
      newTemplate,
      clientNameStr,
      userNameStr,
      deceasedNameStr,
      serviceTypeStr,
      topicStr
    );
    setWhatsappMessage(compiled);
  };

  const STANDARD_PRICING_TEXT = `Our *most popular package* is our:
*Muzukuru Pro package starting at $699* 
This is a 3 Camera package and includes *shots of the Audience* - this really helps relatives and friends following online to feel that they are part of the service.

Our starting price for a live broadcast including a site visit is $499 - This is our *Muzukuru X Package* with only 2 cameras - *(No Audience shots)* - family and friends really appreciate seeing others there.

We have many additional services should you require them we would be happy to provide these.

*Your first step* is to send us a photo 
(So that we can use the photo to create a cover page for the video and to create a link to share with family and friends.)`;

  const handleAppendPricing = () => {
    setWhatsappMessage((prev) => {
      if (!prev.trim()) return STANDARD_PRICING_TEXT;
      return `${prev.trim()}\n\n${STANDARD_PRICING_TEXT}`;
    });
  };

  const handleGenerateWithAI = async (overridePrompt?: string) => {
    setAiError("");
    setAiLoading(true);

    const apiKey =
      process.env.REACT_APP_GEMINI_API_KEY ||
      process.env.GEMINI_API_KEY ||
      (window as unknown as { GEMINI_API_KEY?: string }).GEMINI_API_KEY;

    if (!apiKey) {
      setAiError(
        "AI feature is currently unavailable because the API key is not configured in the environment."
      );
      setAiLoading(false);
      return;
    }

    const eventDate = bookingData?.streams?.[0]?.date || "N/A";
    const eventTime =
      bookingData?.streams?.[0]?.start && bookingData?.streams?.[0]?.end
        ? `${bookingData.streams[0].start.slice(0, 5)} - ${bookingData.streams[0].end.slice(0, 5)}`
        : "N/A";
    const locationName =
      bookingData?.streams?.[0]?.venueDetails?.name || "N/A";

    const promptText = overridePrompt || aiPrompt;

    const fullPrompt = `You are an AI assistant helping a staff member at Muzukuru Funeral Streaming (a funeral home and event streaming service provider) write a professional, respectful, and clear WhatsApp message to a client.

Context:
- Client Name: ${clientNameStr}
- Staff Member (Your Name): ${userNameStr}
- Topic: ${topicStr}
- Event Title: ${bookingData?.service || "Streaming Service"}
- Deceased Name: ${deceasedNameStr || "N/A"}
- Event Date: ${eventDate}
- Event Time: ${eventTime}
- Location: ${locationName}

Standard Pricing & Package Details (Reference when asked to include pricing, packages, or costs):
- Muzukuru Pro Package (Most Popular): Starting at $699. 3 Camera package, includes shots of the Audience (helps relatives and friends following online feel part of the service).
- Muzukuru X Package: Starting price $499 for live broadcast including a site visit. 2 cameras (No Audience shots).
- Additional Services: We have many additional services should you require them, we would be happy to provide these.
- First Step Required: Client's first step is to send us a photo so we can use it to create a cover page for the video and create a link to share with family and friends.

Current message draft:
"${whatsappMessage}"

User instruction:
"${promptText || "Generate a standard polite message about their streaming booking"}"

Please generate only the message text itself. Do not include any subject line, quotes around the message, or conversational introductory/concluding remarks from you (the AI). Write it in a style suitable for WhatsApp (clear, concise, professional, empathetic, and cleanly formatted using WhatsApp *bold* markers where appropriate).`;

    try {
      const response = await axios.post(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.1-flash-lite:generateContent?key=${apiKey}`,
        {
          contents: [
            {
              parts: [{ text: fullPrompt }],
            },
          ],
        }
      );

      const generated =
        response.data?.candidates?.[0]?.content?.parts?.[0]?.text;
      if (generated) {
        let cleanText = generated.trim();
        if (cleanText.startsWith("```")) {
          cleanText = cleanText
            .replace(/^```[a-zA-Z]*\n/, "")
            .replace(/\n```$/, "");
        }
        setAiGeneratedText(cleanText.trim());
      } else {
        setAiError("Failed to generate content. Please try again.");
      }
    } catch (err: unknown) {
      console.error("API Error", err);
      let errMsg = "An error occurred while communicating with AI.";
      if (axios.isAxiosError(err)) {
        errMsg = err.response?.data?.error?.message || err.message;
      } else if (err instanceof Error) {
        errMsg = err.message;
      }
      setAiError(errMsg);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveTemplateAndSend = () => {
    if (showTemplateEditor) {
      localStorage.setItem("whatsapp_booking_template", whatsappTemplate);
    }

    const phoneValue =
      client?.phone && client.phone !== "N/A" ? client.phone : "";
    const normalizedPhone = normalizePhoneForWhatsapp(phoneValue);
    const encoded = encodeURIComponent(whatsappMessage || "");
    const url = `https://wa.me/${normalizedPhone}?text=${encoded}`;
    window.open(url, "_blank", "noopener,noreferrer");
    onClose();
  };

  const [sendingBusiness, setSendingBusiness] = useState(false);

  const handleSendMuzukuruBusiness = async () => {
    if (showTemplateEditor) {
      localStorage.setItem("whatsapp_booking_template", whatsappTemplate);
    }

    const phoneValue = client?.phone && client.phone !== "N/A" ? client.phone : "";
    const normalizedPhone = normalizePhoneForWhatsapp(phoneValue);

    if (!normalizedPhone) {
      showErrorToast("Client phone number is missing or invalid.");
      return;
    }

    setSendingBusiness(true);
    try {
      const res = await apiService.post<SendWhatsAppResponse>("/api/whatsapp/send-direct/", {
        recipient: normalizedPhone,
        message: whatsappMessage,
        booking_id: bookingData?.id,
      });
      if (res.data?.ok) {
        showSuccessToast(res.data.message || "Message sent via Muzukuru Business WhatsApp!");
        onClose();
      } else {
        showErrorToast(res.data?.error || "Failed to send message via Muzukuru Business.");
      }
    } catch (err: unknown) {
      console.error("WhatsApp Business API error:", err);
      let errMsg = "Failed to send message via Muzukuru Business.";
      if (axios.isAxiosError(err)) {
        errMsg = err.response?.data?.error || err.message;
      } else if (err instanceof Error) {
        errMsg = err.message;
      }
      showErrorToast(errMsg);
    } finally {
      setSendingBusiness(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
          bgcolor: colors.primary[900],
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, color: colors.primary[700] }}>
        Send WhatsApp Message
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Send a pre-filled WhatsApp message to <strong>{client?.name}</strong>{" "}
          ({client?.phone}).
        </Typography>

        <TextField
          label="Message Content"
          multiline
          rows={4}
          fullWidth
          variant="outlined"
          value={whatsappMessage}
          onChange={(e) => setWhatsappMessage(e.target.value)}
          sx={{
            mb: 2,
            "& .MuiInputBase-root": {
              color: colors.primary[700],
            },
            "& .MuiInputLabel-root": {
              color: colors.primary[300],
            },
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Button
            startIcon={<AutoAwesomeIcon />}
            onClick={() => setAiPanelOpen(!aiPanelOpen)}
            sx={{
              bgcolor: "#614338",
              color: "white",
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              py: 0.8,
              textTransform: "none",
              boxShadow: "0 3px 5px 2px rgba(97, 67, 56, .2)",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                bgcolor: "#4d342c",
                transform: "scale(1.02)",
              },
            }}
          >
            {aiPanelOpen ? "Close AI Assistant" : "Write with AI"}
          </Button>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Button
              size="small"
              onClick={handleAppendPricing}
              sx={{
                textTransform: "none",
                color: "#614338",
                fontWeight: 600,
                border: "1px dashed rgba(97, 67, 56, 0.4)",
                px: 1.5,
                borderRadius: 1.5,
                "&:hover": { bgcolor: "rgba(97, 67, 56, 0.05)" },
              }}
            >
              + Insert Standard Pricing
            </Button>

            {whatsappMessage !== originalMessage && (
              <Button
                size="small"
                startIcon={<RestoreIcon />}
                onClick={() => setWhatsappMessage(originalMessage)}
                sx={{ textTransform: "none", color: "text.secondary" }}
              >
                Reset to Original
              </Button>
            )}
          </Box>
        </Box>

        <Collapse in={aiPanelOpen} timeout="auto" unmountOnExit>
          <Box
            sx={{
              p: 2.5,
              mb: 3,
              borderRadius: 3,
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "rgba(97, 67, 56, 0.04)",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(97, 67, 56, 0.12)"
              }`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.8,
                  color: colors.primary[700],
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 18, color: "#614338" }} />
                Muzukuru AI Assistant
              </Typography>
            </Box>

            <TextField
              label="What would you like the AI to write/change?"
              placeholder="e.g., Make it sound more empathetic, translate to Shona, include package pricing options..."
              multiline
              rows={2}
              fullWidth
              variant="outlined"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              sx={{
                mb: 1.5,
                "& .MuiInputBase-root": {
                  color: colors.primary[700],
                },
                "& .MuiInputLabel-root": {
                  color: colors.primary[300],
                },
              }}
              disabled={aiLoading}
            />

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {[
                {
                  label: "🏷️ Include Pricing",
                  prompt:
                    "Include our standard package pricing details ($699 Muzukuru Pro package starting price with 3 cameras & audience shots, $499 Muzukuru X package with 2 cameras), mention additional services are available, and ask them to send us a photo as the first step for the video cover page and sharing link.",
                },
                {
                  label: "💼 Professional",
                  prompt:
                    "Make this message sound more professional, polite, and official.",
                },
                {
                  label: "❤️ Empathetic",
                  prompt:
                    "Make this message very warm, supportive, and empathetic.",
                },
                {
                  label: "⚡ Short & Direct",
                  prompt: "Make this message extremely concise and to the point.",
                },
                {
                  label: "📞 Request Callback",
                  prompt:
                    "Add a request asking them to call us back when they get a chance.",
                },
              ].map((item) => (
                <Chip
                  key={item.label}
                  label={item.label}
                  onClick={() => {
                    setAiPrompt(item.prompt);
                    handleGenerateWithAI(item.prompt);
                  }}
                  disabled={aiLoading}
                  clickable
                  size="small"
                  sx={{
                    bgcolor: "background.paper",
                    color: colors.primary[700],
                    border: "1px solid",
                    borderColor: "divider",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                />
              ))}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Button
                onClick={() => handleGenerateWithAI()}
                variant="contained"
                disabled={aiLoading}
                startIcon={
                  aiLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <AutoAwesomeIcon />
                  )
                }
                sx={{
                  bgcolor: "#614338",
                  color: "white",
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                  "&:hover": {
                    bgcolor: "#4d342c",
                  },
                }}
              >
                {aiLoading ? "Generating..." : "Generate with AI"}
              </Button>
            </Box>

            {aiError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {aiError}
              </Alert>
            )}

            {aiGeneratedText && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  position: "relative",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  fontWeight={600}
                  mb={1}
                >
                  AI Suggestion:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", mb: 2, color: colors.primary[700] }}
                >
                  {aiGeneratedText}
                </Typography>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={() => setAiGeneratedText("")}
                    sx={{ textTransform: "none", color: colors.primary[700] }}
                  >
                    Discard
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      setWhatsappMessage(aiGeneratedText);
                      setAiGeneratedText("");
                      setAiPanelOpen(false);
                    }}
                    sx={{
                      textTransform: "none",
                      bgcolor: "#25D366",
                      color: "white",
                      "&:hover": { bgcolor: "#128C7E" },
                    }}
                  >
                    Apply Message
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>

        <Box sx={{ mb: 1 }}>
          <Button
            size="small"
            onClick={() => setShowTemplateEditor(!showTemplateEditor)}
            sx={{ textTransform: "none", color: colors.primary[700] }}
          >
            {showTemplateEditor
              ? "Hide Template Settings"
              : "Configure Default Template"}
          </Button>
        </Box>

        {showTemplateEditor && (
          <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} mb={1} color={colors.primary[700]}>
              Default Message Template
            </Typography>
            <TextField
              label="Template String"
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              value={whatsappTemplate}
              onChange={(e) => handleTemplateChange(e.target.value)}
              helperText="Use placeholders: [Client Name], [Your Name], [Deceased Name], [Service Type], [Topic]"
              sx={{
                mb: 1,
                "& .MuiInputBase-root": {
                  color: colors.primary[700],
                },
                "& .MuiInputLabel-root": {
                  color: colors.primary[300],
                },
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Example: Hi [Client Name], I am [Your Name] from Muzukuru regarding [Topic].
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit" sx={{ color: colors.primary[700] }}>
          Cancel
        </Button>
        <Button
          onClick={handleSendMuzukuruBusiness}
          disabled={sendingBusiness}
          variant="outlined"
          startIcon={
            sendingBusiness ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <StorefrontIcon />
            )
          }
          sx={{
            borderColor: "#724E41",
            color: theme.palette.mode === "dark" ? "#f59e0b" : "#724E41",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              borderColor: "#5a3d33",
              bgcolor: "rgba(114, 78, 65, 0.08)",
            },
          }}
        >
          {sendingBusiness ? "Sending..." : "Send via Muzukuru Business"}
        </Button>
        <Button
          onClick={handleSaveTemplateAndSend}
          variant="contained"
          startIcon={<WhatsAppIcon />}
          sx={{
            bgcolor: "#25D366",
            color: "#fff",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": {
              bgcolor: "#128C7E",
            },
          }}
        >
          Open WhatsApp
        </Button>
      </DialogActions>
    </Dialog>
  );
}
